import { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  Container,
  VStack,
  HStack,
  Box,
  Heading,
  Text,
  Input,
  Textarea,
  Button,
  NativeSelect,
  Flex,
  Alert,
  useDisclosure,
  Field,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiPlus, FiEye, FiSave } from 'react-icons/fi'
import { FilterGroupCard, LiveCountBadge, PreviewDrawer, toEvaluationRequest } from '../components/listBuilder'
import {
  useContactListDetail,
  useCreateContactList,
  useUpdateContactList,
  useFilterFieldMetadata,
  useAvailableTags,
} from '../hooks/useContactList'
import { useIsMobile } from '../hooks/useIsMobile'
import { StickyActionFooter, BackLink } from '../components/shared'
import { useReturnTo } from '../lib/returnNav'
import type { FilterGroup, FilterOperator, ListRefreshStrategy } from '../types/contactList'
import { uuid } from '../lib/uuid'

export function ListBuilderPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const isEditing = !!id
  const isMobile = useIsMobile()
  const back = useReturnTo({ to: '/dashboard/lists', label: 'Lists' })

  // Load metadata
  const { data: fields = [] } = useFilterFieldMetadata()
  const { data: tags = [] } = useAvailableTags()

  // Load existing list if editing
  const { data: existingList, isLoading: isLoadingList } = useContactListDetail(id)

  // Mutations
  const createMutation = useCreateContactList()
  const updateMutation = useUpdateContactList(id ?? '')

  // Preview drawer
  const { open: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure()

  // Form state
  const [label, setLabel] = useState('')
  const [description, setDescription] = useState('')
  const [refreshStrategy, setRefreshStrategy] = useState<ListRefreshStrategy>('Manual')
  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>([
    createEmptyGroup(0),
  ])

  // Populate when editing an existing list
  useEffect(() => {
    if (existingList) {
      setLabel(existingList.label)
      setDescription(existingList.description ?? '')
      setRefreshStrategy(existingList.refreshStrategy as ListRefreshStrategy)
      if (existingList.filterGroups.length > 0) {
        setFilterGroups(
          existingList.filterGroups.map(g => ({
            ...g,
            filters: g.filters.map(f => ({
              ...f,
              id: f.id || uuid(),
            })),
          }))
        )
      }
    }
  }, [existingList])

  // Seed from a deep-link preset (e.g., the analytics map's "Export list" action) when creating a new list.
  const presetAppliedRef = useRef(false)
  useEffect(() => {
    if (isEditing || presetAppliedRef.current) return
    const state = location.state as { presetFilters?: FilterGroup[]; presetName?: string } | null
    if (!state?.presetFilters) return
    presetAppliedRef.current = true
    setFilterGroups(
      state.presetFilters.map(g => ({
        ...g,
        filters: g.filters.map(f => ({
          ...f,
          id: f.id || uuid(),
        })),
      }))
    )
    setLabel(state.presetName ?? '')
  }, [isEditing, location.state])

  const handleGroupChange = useCallback((index: number, updated: FilterGroup) => {
    setFilterGroups(prev => {
      const next = [...prev]
      next[index] = updated
      return next
    })
  }, [])

  const handleRemoveGroup = useCallback((index: number) => {
    setFilterGroups(prev => {
      const next = prev.filter((_, i) => i !== index)
      return next.map((g, i) => ({ ...g, groupIndex: i }))
    })
  }, [])

  const handleAddGroup = useCallback(() => {
    setFilterGroups(prev => [...prev, createEmptyGroup(prev.length)])
  }, [])

  const handleSave = async () => {
    if (!label.trim()) {
      toaster.create({ title: 'Please enter a name', type: 'warning', duration: 3000 })
      return
    }

    const request = {
      label: label.trim(),
      description: description.trim() || undefined,
      refreshStrategy,
      filterGroups: filterGroups.map(g => ({
        groupIndex: g.groupIndex,
        filters: g.filters
          .filter(f => f.fieldName)
          .map((f, i) => ({
            fieldName: f.fieldName,
            operator: f.operator,
            value: f.value,
            displayOrder: i,
          })),
      })),
    }

    try {
      if (isEditing) {
        await updateMutation.mutateAsync(request)
        toaster.create({ title: 'Contact list updated', type: 'success', duration: 3000 })
      } else {
        await createMutation.mutateAsync(request)
        toaster.create({ title: 'Contact list created', type: 'success', duration: 3000 })
        navigate('/dashboard/lists', { replace: true })
      }
    } catch {
      toaster.create({ title: 'Failed to save', type: 'error', duration: 5000 })
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending
  const previewRequest = toEvaluationRequest(filterGroups)

  if (isEditing && isLoadingList) {
    return (
      <Container maxW="container.lg" py={8}>
        <Text color="gray.500">Loading contact list...</Text>
      </Container>
    )
  }

  return (
    <Container maxW="container.lg" px={{ base: 4, md: 8 }} py={{ base: 4, md: 8 }}>
      <VStack gap={6} align="stretch" pb={{ base: '96px', md: 8 }}>
        {/* Header */}
        <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
          <HStack gap={3}>
            {back && <BackLink to={back.to} label={back.label} />}
          </HStack>
          <Box>
            <Heading size="lg">{isEditing ? 'Edit Contact List' : 'New Contact List'}</Heading>
            <Text color="gray.500" mt={1}>
              Define filter groups to build a contact list
            </Text>
          </Box>
        </Flex>

        {/* Live count + Preview (sticky under top bar on mobile) */}
        <Box
          position={{ base: 'sticky', md: 'static' }}
          top={{ base: 'calc(68px + env(safe-area-inset-top, 0px))', md: 'auto' }}
          zIndex={{ base: 8, md: 'auto' }}
          bg={{ base: 'white', md: 'transparent' }}
          py={2}
        >
          <HStack gap={3} justify={{ base: 'space-between', md: 'flex-end' }}>
            <LiveCountBadge filterGroups={filterGroups} />
            <Button
              variant="outline"
              size="sm"
              onClick={onPreviewOpen}
              disabled={!previewRequest}><FiEye />Preview
                          </Button>
          </HStack>
        </Box>

        {/* Metadata Card */}
        <Box bg="surface" p={6} borderRadius="lg" shadow="md" borderWidth="1px" borderColor="gray.200">
          <VStack gap={4} align="stretch">
            <Field.Root required>
              <Field.Label>Name</Field.Label>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g., Active Voters in Virginia"
              />
            </Field.Root>
            <Field.Root>
              <Field.Label>Description</Field.Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description of this list's purpose..."
                rows={2}
              />
            </Field.Root>
            <Field.Root>
              <Field.Label>Refresh Strategy</Field.Label>
              <NativeSelect.Root size="sm">
                <NativeSelect.Field
                  value={refreshStrategy}
                  onChange={(e) => setRefreshStrategy(e.target.value as ListRefreshStrategy)}
                  maxW="250px">
                  <option value="Dynamic">Dynamic (always live)</option>
                  <option value="Manual">Manual (refresh on demand)</option>
                  <option value="AutoDaily">Auto Daily</option>
                  <option value="Locked">Locked (snapshot)</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Field.Root>
          </VStack>
        </Box>

        {/* Filter Groups */}
        <Box>
          <Heading size="md" mb={4}>Filter Groups</Heading>
          {fields.length === 0 && (
            <Alert.Root status="info" borderRadius="md">
              <Alert.Indicator />
              Loading available filter fields...
            </Alert.Root>
          )}
          <VStack gap={4} align="stretch">
            {filterGroups.map((group, index) => (
              <Box key={group.groupIndex}>
                {index > 0 && (
                  <Flex justify="center" my={2}>
                    <Text
                      fontSize="sm"
                      fontWeight="bold"
                      color="orange.500"
                      bg="orange.50"
                      px={4}
                      py={1}
                      borderRadius="full"
                    >
                      OR
                    </Text>
                  </Flex>
                )}
                <FilterGroupCard
                  group={group}
                  fields={fields}
                  tags={tags}
                  onChange={(updated) => handleGroupChange(index, updated)}
                  onRemove={() => handleRemoveGroup(index)}
                  canRemove={filterGroups.length > 1}
                />
              </Box>
            ))}

            <Button
              variant="outline"
              colorPalette="orange"
              onClick={handleAddGroup}
              alignSelf="flex-start"><FiPlus />Add OR group
                          </Button>
          </VStack>
        </Box>

        {/* Action Bar */}
        {isMobile ? (
          <StickyActionFooter>
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard/lists')}
            >
              Cancel
            </Button>
            <Button
              colorPalette="brand"
              onClick={handleSave}
              loading={isSaving}
              disabled={!label.trim()}
            >
              {isEditing ? 'Save Changes' : 'Create List'}
            </Button>
          </StickyActionFooter>
        ) : (
          <HStack gap={4} pt={4} borderTopWidth="1px" borderColor="gray.200">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard/lists')}
            >
              Cancel
            </Button>
            <Button
              colorPalette="brand"
              onClick={handleSave}
              loading={isSaving}
              disabled={!label.trim()}><FiSave />{isEditing ? 'Save Changes' : 'Create List'}</Button>
          </HStack>
        )}
      </VStack>

      {/* Preview Drawer */}
      <PreviewDrawer
        isOpen={isPreviewOpen}
        onClose={onPreviewClose}
        request={previewRequest}
      />
    </Container>
  );
}

function createEmptyGroup(index: number): FilterGroup {
  return {
    groupIndex: index,
    filters: [
      {
        id: uuid(),
        fieldName: '',
        operator: 'Equals' as FilterOperator,
        value: undefined,
        displayOrder: 0,
      },
    ],
  }
}
